import { Info, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { cn } from '@kioskgaming/ui';
import type { DashboardKpi } from '../../services/api';

type Props = {
  label: string;
  tooltip: string;
  kpi?: DashboardKpi | null;
  formatValue: (n: number) => string;
  invertDelta?: boolean;
};

function DeltaBadge({ deltaPct, invert }: { deltaPct: number | null; invert: boolean }) {
  if (deltaPct === null) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
        N/A
      </span>
    );
  }
  const isFlat = Math.abs(deltaPct) < 0.05;
  const isImprovement = invert ? deltaPct < 0 : deltaPct > 0;
  const Icon = isFlat ? Minus : deltaPct > 0 ? TrendingUp : TrendingDown;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold',
        isFlat
          ? 'bg-muted text-muted-foreground'
          : isImprovement
            ? 'bg-emerald-50 text-emerald-700'
            : 'bg-red-50 text-red-700',
      )}
    >
      <Icon className="h-3 w-3" />
      {deltaPct > 0 ? '+' : ''}
      {deltaPct.toLocaleString('en-US', { maximumFractionDigits: 1 })}%
    </span>
  );
}

/** Admin-style headline KPI card (large value, blue border). */
export default function HeadlineCard({ label, tooltip, kpi, formatValue, invertDelta = false }: Props) {
  const value = kpi?.value ?? 0;
  const isNegative = value < 0;

  return (
    <div className="rounded-xl border border-blue-200 bg-violet-50/50 p-5" title={tooltip}>
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
        <Info className="h-4 w-4 text-blue-400" aria-hidden />
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div
          className={cn(
            'text-3xl font-bold tabular-nums tracking-tight',
            isNegative ? 'text-red-700' : 'text-violet-900',
          )}
        >
          {kpi != null ? formatValue(value) : '—'}
        </div>
        {kpi?.deltaPct !== undefined && (
          <DeltaBadge deltaPct={kpi.deltaPct ?? null} invert={invertDelta} />
        )}
      </div>
    </div>
  );
}
